/**
 * Settings Controller Module
 * Handles settings and profile management logic
 */

import type { State } from './types';
import { els } from './modules/ui';
import { displayCreditBalanceInOptions } from './modules/credit';

/**
 * Populate timezone dropdowns with available timezones
 */
export function populateTimeZones(state: State): void {
  const targets: HTMLSelectElement[] = [];
  if (els.settingsTz instanceof HTMLSelectElement) {
    targets.push(els.settingsTz);
  }
  if (els.firstRunTimeZone instanceof HTMLSelectElement) {
    targets.push(els.firstRunTimeZone);
  }
  if (targets.length === 0) return;

  let zones: string[] = [];
  try {
    if (Intl && typeof (Intl as any).supportedValuesOf === 'function') {
      zones = (Intl as any).supportedValuesOf('timeZone') || [];
    }
  } catch {
    zones = [];
  }

  if (!Array.isArray(zones) || zones.length === 0) {
    zones = [
      'UTC',
      'America/New_York',
      'America/Chicago',
      'America/Denver',
      'America/Los_Angeles',
      'Europe/London',
    ];
  }

  targets.forEach(select => {
    if (!select) return;
    if (select.options.length === 0) {
      zones.forEach(z => {
        const opt = document.createElement('option');
        opt.value = z;
        opt.textContent = z;
        select.appendChild(opt);
      });
    }
    const current = state.settings && state.settings.timeZone;
    if (current) {
      select.value = current;
    }
  });
}

/**
 * Show first-run modal if profile is incomplete
 */
import type { PortalUser } from './types';

export function maybeShowFirstRunModal(prof: PortalUser): void {
  if (!prof) return;

  const hasCompany = prof.company && prof.company.trim().length > 0;
  const hasTimeZone = prof.timeZone && prof.timeZone.trim().length > 0;
  const hasPostalCode = prof.homePostalCode && prof.homePostalCode.trim().length > 0;

  // If all required fields are present, don't show the modal
  if (hasCompany && hasTimeZone && hasPostalCode) {
    return;
  }

  // Populate existing values if available
  if (els.firstRunCompany && prof.company) {
    (els.firstRunCompany as HTMLInputElement).value = prof.company;
  }
  if (els.firstRunTimeZone && prof.timeZone) {
    (els.firstRunTimeZone as HTMLSelectElement).value = prof.timeZone;
  }
  if (els.firstRunZip && prof.homePostalCode) {
    (els.firstRunZip as HTMLInputElement).value = prof.homePostalCode;
  }

  // Show the modal
  if (els.firstRunModal) {
    populateTimeZones({} as State); // Pass empty state just for timezone population
    els.firstRunModal.hidden = false;
    document.body.classList.add('modal-open');
  }
}
